import { useEffect } from 'react';

import type { Interaction } from '@/types';

/**
 * While the credential drawer is open on a specific request, advance the
 * selection to the next pending request once the current one leaves
 * `pendingInteractions` (answered or expired).
 *
 * This closes the concurrent-request gap left by {@link useAutoOpenCredentialDrawer}
 * (ENG-4571 §2): after the operator answers one deadline-bound HITL credential
 * prompt, the next still-pending one is surfaced as a form in place — so a second
 * concurrent request isn't stranded on the passive toast/roster path within its
 * hard ~10-minute window. When none remain, it clears the selection so the drawer
 * shows its own "no pending requests" view.
 *
 * Two properties are deliberately preserved:
 * - **Never advances off a form that is still answerable.** It advances only
 *   once the SELECTED request has left the pending set — never while it is still
 *   pending (still fillable), and never to a *sibling* that merely arrived
 *   alongside it (the sibling case is the accepted tradeoff pinned by
 *   `useAutoOpenCredentialDrawer`'s test). A selected request leaving the pending
 *   set for any terminal reason DOES advance the selection — that includes the
 *   operator answering it AND its ~10-minute backend expiry, so an unsubmittable
 *   expired form is replaced by the next live request.
 * - **Never fights a manual close.** It runs only while the drawer is open with a
 *   form selected; closing the drawer (`selectedRequestKey`/`isDrawerOpen` gate)
 *   stops it, so a manual dismissal is respected.
 *
 * @param pendingInteractions all pending requests across the conversation tree
 * @param isDrawerOpen        whether the credential drawer is currently open
 * @param selectedRequestKey  the request currently shown as a form (null = roster)
 * @param onSelectRequest     selects a request (null shows the roster/empty view)
 */
export function useAdvanceCredentialSelection(
  pendingInteractions: Interaction[],
  isDrawerOpen: boolean,
  selectedRequestKey: string | null,
  onSelectRequest: (key: string | null) => void
) {
  useEffect(() => {
    // Only act while a form is open. On the roster (null) there is nothing to
    // advance, and while closed we never reopen (respect the manual close).
    if (!isDrawerOpen || selectedRequestKey === null) return;

    // Still filling the selected request — do not move.
    const stillPending = pendingInteractions.some(
      interaction => interaction.key === selectedRequestKey
    );
    if (stillPending) return;

    // Selected request was answered/removed: advance to the oldest remaining
    // pending request, or clear to the roster/empty view when none remain.
    onSelectRequest(pendingInteractions[0]?.key ?? null);
  }, [pendingInteractions, isDrawerOpen, selectedRequestKey, onSelectRequest]);
}
